
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Edit, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { compressImage, uploadToStorage } from '@/lib/imageCompression';

interface ImageUploaderProps {
  type: 'profile' | 'cover';
  currentImage?: string;
  onImageUpdate: (type: 'profile' | 'cover', url: string) => void;
  className?: string;
  iconClassName?: string;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  type,
  currentImage,
  onImageUpdate,
  className,
  iconClassName
}) => {
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);

  const handleImageUpload = async (file: File) => {
    if (!user) return;

    setIsUploading(true);
    try {
      const compressed = await compressImage(file);
      const publicUrl = await uploadToStorage(compressed, user.id, 'profile-images');
      onImageUpdate(type, publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImageClick = () => {
    if (isUploading) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleImageUpload(file);
      }
    };
    input.click();
  };

  return (
    <Button
      variant="outline"
      size="icon"
      className={className}
      onClick={handleImageClick}
      disabled={isUploading}
    >
      {isUploading ? (
        <Loader2 className={`${iconClassName} animate-spin`} />
      ) : (
        <Edit className={iconClassName} />
      )}
    </Button>
  );
};
